import type { ReactNode } from 'react';
import { TrendingDown, UserX, UserPlus } from 'lucide-react';
import type { ConnectorExecution, LeafAbsence } from '@/types/connectors';
import { useOutputFiles } from '@/hooks/useOutputFiles';

function formatBytes(b?: number | null): string {
  if (!b) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function num(n?: number | null): string { return n == null ? '—' : n.toLocaleString(); }

/** Group `la.missing` by `la.severities` (#872), falling back to a flat CID
 * listing when no per-CID severities are present. Preserves the roster-size/
 * prior-runs suffix from the original single-line tooltip. */
function buildLeafAbsenceTitle(la: LeafAbsence): string {
  const suffix = `roster of ${la.roster_size} from the last ${la.prior_runs} successful runs`;
  const severities = la.severities ?? {};
  if (Object.keys(severities).length === 0) {
    return `Missing expected client(s): ${la.missing.join(', ')} (${suffix})`;
  }
  const groups: Record<'critical' | 'high' | 'normal', string[]> = {
    critical: [],
    high: [],
    normal: [],
  };
  for (const cid of la.missing) {
    const level = severities[cid] === 'critical' || severities[cid] === 'high' ? severities[cid] : 'normal';
    groups[level as 'critical' | 'high' | 'normal'].push(cid);
  }
  const parts = (['critical', 'high', 'normal'] as const)
    .filter((level) => groups[level].length > 0)
    .map((level) => `${level[0].toUpperCase()}${level.slice(1)}: ${groups[level].join(', ')}`);
  return `Missing expected client(s) — ${parts.join('; ')} (${suffix})`;
}

interface Props { connectorType: string; execution: ConnectorExecution; }

export function ExecutionStats({ connectorType, execution }: Props) {
  // Share the cached output-files query with DatasetOutputGroups (same options).
  // For DuckLake-backed runs, execution.bytes_written is 0 and the history
  // endpoint drops output_paths, so we derive Bytes/Tables from output-files.
  const { data } = useOutputFiles(
    connectorType,
    execution.execution_id,
    { include_schema: true },
    Boolean(execution.execution_id && execution.status === 'success'),
  );
  const totalSize = data?.total_size_bytes ?? execution.bytes_written ?? 0;
  const filesCount = data?.total_files ?? execution.files_created ?? null;
  // #1009: a connector that merges instead of writing files (google_sheets)
  // has no output_paths on the /history payload and no output-files response,
  // so the derived count is 0 for a run that really did write tables. Fall
  // back to the stored counter — the derived value still wins when present.
  const derivedTables = data?.total_tables ?? (execution.output_paths ?? []).length;
  const tablesCount = derivedTables || (execution.tables_updated ?? 0);
  const deleted = execution.deleted_rows;
  const leafCount = execution.discovered_leaf_count;
  const leafDrop = execution.leaf_count_drop;
  const leafAbsence = execution.leaf_absence;
  const leafAppearance = execution.leaf_appearance;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Tile label="Records"  value={num(execution.records_processed)} />
      <Tile label={data?.total_files != null ? 'Table files' : 'Files'} value={num(filesCount)} />
      <Tile label="Size"     value={totalSize ? formatBytes(totalSize) : '—'} />
      <Tile label="Datasets" value={tablesCount > 0 ? tablesCount.toLocaleString() : '—'} />
      {deleted != null && deleted > 0 && (
        <Tile label="Deleted" value={deleted.toLocaleString()} />
      )}
      {leafCount != null && (
        <Tile
          label="Leaf accounts"
          value={leafCount.toLocaleString()}
          warn={(leafDrop?.detected ?? false) || (leafAbsence?.detected ?? false) || (leafAppearance?.detected ?? false)}
          badge={
            (leafDrop?.detected || leafAbsence?.detected || leafAppearance?.detected) ? (
              <div className="mt-1 flex flex-col items-start gap-1">
                {leafDrop?.detected && (
                  <span
                    className="inline-flex items-center gap-1 text-xs font-medium text-warning"
                    title={`Discovered ${leafDrop.current} vs a baseline of ${Math.round(leafDrop.baseline)} (median of the last ${leafDrop.prior_runs} successful runs)`}
                  >
                    <TrendingDown className="h-3.5 w-3.5" aria-hidden="true" />
                    {Math.round(leafDrop.drop_pct)}% vs {Math.round(leafDrop.baseline)}
                  </span>
                )}
                {leafAbsence?.detected && (
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-medium ${
                      leafAbsence.max_severity === 'critical' ? 'text-destructive' : 'text-warning'
                    }`}
                    title={buildLeafAbsenceTitle(leafAbsence)}
                  >
                    <UserX className="h-3.5 w-3.5" aria-hidden="true" />
                    {leafAbsence.missing.length} missing
                    {leafAbsence.max_severity && leafAbsence.max_severity !== 'normal'
                      ? ` (${leafAbsence.max_severity})`
                      : ''}
                  </span>
                )}
                {leafAppearance?.detected && (
                  <span
                    className="inline-flex items-center gap-1 text-xs font-medium text-warning"
                    title={`Unexpected new client(s): ${leafAppearance.unexpected.join(', ')} (not seen in the last ${leafAppearance.prior_runs} successful runs)`}
                  >
                    <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
                    {leafAppearance.unexpected.length} new
                  </span>
                )}
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  warn = false,
  badge,
}: {
  label: string;
  value: string;
  warn?: boolean;
  badge?: ReactNode;
}) {
  return (
    <div className={`rounded-md border bg-background p-3${warn ? ' border-warning' : ''}`}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1.5 text-2xl font-bold tracking-tight">{value}</div>
      {badge}
    </div>
  );
}
